// ── Outbox: изменения, которые ещё не дошли до Supabase ──────────────────────
// Каждая запись (карточки, повторение, проекты, тексты, слова) сначала
// сохраняется локально и кладётся сюда. flushOutbox() отправляет очередь по
// порядку и убирает операцию только после ответа сервера «ок». Поэтому
// изменения не теряются без интернета, при обрыве связи или ошибке сервера,
// а синхронизация не перетирает то, что ещё не отправлено.
//
// Операция: { kind, key, data, at }
//   cards.upsert   key card:<id>        data: строка таблицы cards (можно частично)
//   cards.delete   key card:<id>        data: { id }
//   project.update key project:<id>     data: { id, fields }
//   project.delete key project:<id>     data: { id, audio_path }
//   text.upsert    key text:<id>        data: полная строка text_projects
//   text.patch     key text:<id>        data: { id, fields }      (например, страница)
//   text.delete    key text:<id>        data: { id }
//   word.upsert    key word:<word>      data: строка user_words
//   word.delete    key word:<word>      data: { word }
// Новая операция с тем же key заменяет старую (две правки карточки → одна).

import { supabase } from './supabase';

import { t } from './i18n';
const KEY = 'ac_outbox_v1';
const BATCH = 500; // строк в одном upsert

const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } };
const save = (q) => { try { localStorage.setItem(KEY, JSON.stringify(q)); } catch {} };

export const getOutbox = () => load();
export const outboxSize = () => load().length;

// Добавить операцию. Правила слияния по key:
//   upsert после upsert → берём новую строку, но сохраняем поля старой, которых нет в новой;
//   patch после upsert  → вливаем поля в upsert;  patch после patch → объединяем поля;
//   delete              → заменяет всё по этому key;
//   project.update      → объединяем поля.
export function enqueue(kind, key, data) {
  const q = load();
  const i = q.findIndex(op => op.key === key);
  const at = Date.now();
  if (i >= 0) {
    const prev = q[i];
    let merged = null;
    if (kind.endsWith('.delete')) merged = { kind, key, data, at };
    else if (kind === prev.kind && kind.endsWith('.upsert')) merged = { kind, key, data: { ...prev.data, ...data }, at };
    else if (kind === 'text.patch' && prev.kind === 'text.upsert') merged = { kind: 'text.upsert', key, data: { ...prev.data, ...data.fields }, at };
    else if (kind === prev.kind && (kind === 'text.patch' || kind === 'project.update')) {
      merged = { kind, key, data: { ...data, fields: { ...prev.data.fields, ...data.fields } }, at };
    }
    if (merged) {
      // Слитую операцию ставим в конец: её порядок относительно других — время последней правки.
      q.splice(i, 1);
      q.push(merged);
      save(q);
      return;
    }
    if (prev.kind.endsWith('.delete') && !kind.endsWith('.delete')) {
      // Запись удалили, а потом снова создали (Undo) — отменяем удаление.
      q.splice(i, 1);
    }
  }
  q.push({ kind, key, data, at });
  save(q);
}

// Что ещё не отправлено: чтобы синхронизация не перетёрла локальные правки.
export function pendingIds() {
  const cards = new Set(), projects = new Set(), texts = new Set(), words = new Set();
  for (const op of load()) {
    const [type, id] = [op.key.slice(0, op.key.indexOf(':')), op.key.slice(op.key.indexOf(':') + 1)];
    if (type === 'card') cards.add(id);
    else if (type === 'project') projects.add(id);
    else if (type === 'text') texts.add(id);
    else if (type === 'word') words.add(id);
  }
  return { cards, projects, texts, words };
}

// Ошибка, при которой повтор не поможет (нарушение схемы, доступа и т.п.): операцию выбрасываем.
// Сеть, 401 (протух токен), 408, 429 и 5xx — временные: оставляем и пробуем позже.
const isPermanent = (status) => status >= 400 && status < 500 && ![401, 408, 429].includes(status);

async function run(ops) {
  const op = ops[0];
  const t = op.kind;
  if (t === 'cards.upsert' || t === 'word.upsert') {
    const table = t === 'cards.upsert' ? 'cards' : 'user_words';
    const conflict = t === 'cards.upsert' ? 'id' : 'user_id,word';
    // Строки с разным набором колонок нельзя слать одним upsert: группируем по колонкам.
    const groups = new Map();
    for (const o of ops) {
      const cols = Object.keys(o.data).sort().join(',');
      if (!groups.has(cols)) groups.set(cols, []);
      groups.get(cols).push(o.data);
    }
    for (const rows of groups.values()) {
      const r = await supabase.from(table).upsert(rows, { onConflict: conflict });
      if (r.error) return r;
    }
    return { error: null };
  }
  if (t === 'cards.delete') return supabase.from('cards').delete().in('id', ops.map(o => o.data.id));
  if (t === 'word.delete') {
    const { data: s } = await supabase.auth.getSession();
    const uid = s?.session?.user?.id;
    if (!uid) return { error: { message: 'no session' }, status: 401 };
    return supabase.from('user_words').delete().eq('user_id', uid).in('word', ops.map(o => o.data.word));
  }
  if (t === 'project.update') return supabase.from('projects').update(op.data.fields).eq('id', op.data.id);
  if (t === 'project.delete') {
    const { id, audio_path } = op.data;
    let r = await supabase.from('cards').delete().eq('project_id', id);
    if (r.error) return r;
    if (audio_path) await supabase.storage.from('audio-files').remove([audio_path]); // файла может уже не быть
    return supabase.from('projects').delete().eq('id', id);
  }
  if (t === 'text.upsert') return supabase.from('text_projects').upsert(op.data, { onConflict: 'id' });
  if (t === 'text.patch') return supabase.from('text_projects').update(op.data.fields).eq('id', op.data.id);
  if (t === 'text.delete') return supabase.from('text_projects').delete().eq('id', op.data.id);
  return { error: null }; // неизвестный тип — выбрасываем
}

// Подряд идущие однотипные строки шлём одним запросом.
const batchable = (k) => k === 'cards.upsert' || k === 'cards.delete' || k === 'word.upsert' || k === 'word.delete';

let _flushing = null;

// Запрос к Supabase иногда не отвечает вовсе (ни ответа, ни ошибки — например, библиотека
// ждёт обновления токена). Раньше такой запрос навсегда держал _flushing, и до
// перезагрузки страницы очередь больше не отправлялась. Теперь через OP_TIMEOUT
// операция считается временной ошибкой и повторится позже (все операции можно
// повторять: upsert/update/delete дают тот же результат).
const OP_TIMEOUT = 20000;
const withTimeout = (p) => new Promise((resolve) => {
  const timer = setTimeout(() => resolve({ error: { message: t("нет ответа сервера за {0} с",[OP_TIMEOUT / 1000]) }, status: 0 }), OP_TIMEOUT);
  p.then((r) => { clearTimeout(timer); resolve(r); },
         (e) => { clearTimeout(timer); resolve({ error: { message: e?.message || String(e) }, status: 0 }); });
});

// Отправить очередь. Возвращает { sent, left, error }.
export function flushOutbox() {
  if (_flushing) return _flushing;
  _flushing = (async () => {
    // Сначала уступаем ход: иначе при пустой очереди вся функция (вместе с finally,
    // где _flushing = null) выполнялась синхронно — ещё до того, как _flushing
    // присваивался этот промис. _flushing навсегда оставался «уже выполненным», и
    // все следующие отправки возвращали его, ничего не отправляя: правки уходили на
    // сервер только после перезагрузки приложения.
    await null;
    let sent = 0;
    let lastError = null;
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return { sent, left: outboxSize(), error: 'offline' };
      for (let guard = 0; guard < 1000; guard++) {
        const q = load();
        if (!q.length) break;
        let n = 1;
        if (batchable(q[0].kind)) while (n < q.length && n < BATCH && q[n].kind === q[0].kind) n++;
        const ops = q.slice(0, n);
        const res = await withTimeout(Promise.resolve().then(() => run(ops)));
        if (res?.error) {
          const status = res.status ?? res.error.status ?? 0;
          if (!isPermanent(status)) {
            lastError = res.error.message || 'network';
            // В журнал ошибок (src/errorLog.js): без сети это нормально, остальное — нет.
            if (typeof navigator === 'undefined' || navigator.onLine !== false) {
              console.error(`[Outbox] не отправлено (${ops[0].kind}, статус ${status}): ${lastError}`);
            }
            break;
          }
          console.warn('[Outbox] dropped', ops[0].kind, res.error.message);
        } else {
          sent += ops.length;
        }
        // Убираем отправленные (или безнадёжные) операции. Во время запроса в очередь
        // могли добавиться новые правки тех же записей — их не трогаем.
        const sentAt = new Map(ops.map(o => [o.key, o.at]));
        save(load().filter(o => !(sentAt.has(o.key) && sentAt.get(o.key) === o.at)));
      }
    } finally {
      _flushing = null;
    }
    const left = outboxSize();
    if (left === 0) window.dispatchEvent?.(new CustomEvent('ac:outbox-empty'));
    return { sent, left, error: lastError };
  })();
  return _flushing;
}

let _timer = null;
// Отправить чуть позже: несколько быстрых правок подряд уйдут одним заходом.
export function flushSoon(delay = 1500) {
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => { _timer = null; flushOutbox().catch(() => {}); }, delay);
}

// Сразу как появился интернет или вкладка снова открыта — дошлём хвосты.
if (typeof window !== 'undefined') {
  // Для диагностики (консоль браузера): что ждёт отправки и отправить сейчас.
  window.__acOutbox = { list: getOutbox, flush: flushOutbox };
  window.addEventListener('online', () => flushSoon(500));
  document.addEventListener?.('visibilitychange', () => { if (document.visibilityState === 'visible' && outboxSize()) flushSoon(500); });
}
